import React from 'react';
import { useAuth } from './useAuth';
import LoginPage from '@/components/LoginPage';

interface RequireAuthProps {
  children: React.ReactNode;
  requiredRole?: string;
  requiredRoles?: string[];
}

function LoadingSpinner() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="flex flex-col items-center gap-4">
        <div className="w-12 h-12 border-4 border-jm-navy border-t-jm-yellow rounded-full animate-spin" />
        <p className="text-jm-navy font-work-sans font-medium">Loading Atlas...</p>
      </div>
    </div>
  );
}

function ForbiddenPage({ requiredRole }: { requiredRole: string }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="max-w-md w-full mx-4 text-center">
        <div className="bg-white rounded-2xl shadow-lg p-10">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg className="w-8 h-8 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
            </svg>
          </div>
          <h1 className="font-montserrat text-2xl font-bold text-jm-dark mb-3">Access Denied</h1>
          <p className="text-gray-500 font-work-sans mb-2">
            You don't have permission to view this page.
          </p>
          <p className="text-sm text-gray-400 font-work-sans">
            Required role: <span className="font-semibold text-jm-navy">{requiredRole}</span>
          </p>
          <a
            href="/"
            className="mt-6 inline-block px-6 py-2.5 bg-jm-navy text-white rounded-lg font-work-sans font-medium hover:bg-jm-navy-dark transition-colors"
          >
            Return Home
          </a>
        </div>
      </div>
    </div>
  );
}

export default function RequireAuth({ children, requiredRole, requiredRoles }: RequireAuthProps) {
  const { isAuthenticated, isLoading, user } = useAuth();

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (!isAuthenticated) {
    return <LoginPage />;
  }

  // Fail closed when a role is required. Guarding on `user &&` would skip the
  // check entirely whenever auth resolved as authenticated but without a user
  // object, rendering every gated page to a roleless caller.
  if (requiredRole && (!user || !user.roles.includes(requiredRole))) {
    return <ForbiddenPage requiredRole={requiredRole} />;
  }

  if (requiredRoles && (!user || !requiredRoles.some((r) => user.roles.includes(r)))) {
    return <ForbiddenPage requiredRole={requiredRoles.join(' or ')} />;
  }

  return <>{children}</>;
}
